<script setup>
import { computed, onMounted, ref } from 'vue';
import { useChangelogStore } from '@/stores/changelog';
import PreviewHeader from '@/components/PreviewHeader.vue';

const changelogStore = useChangelogStore();

const entries = computed(() => changelogStore.entries || []);
const activeIndex = ref(0);
const sortOrder = ref('desc'); // 'desc' = Neueste zuerst, 'asc' = Älteste zuerst
const yearFilter = ref('all');

const latestVersion = computed(() => changelogStore.latestVersion);
const latestEntry = computed(() => entries.value[0] || null);
const unseenVersions = ref([]);

const availableYears = computed(() => {
  const years = entries.value
    .map((e) => {
      if (!e.date) return null;
      const d = typeof e.date === 'string' ? new Date(e.date) : e.date;
      if (Number.isNaN(d.getTime())) return null;
      return d.getFullYear();
    })
    .filter((y) => y !== null);
  return Array.from(new Set(years)).sort((a, b) => b - a);
});

const filteredSortedEntries = computed(() => {
  let list = entries.value.slice();

  if (yearFilter.value !== 'all') {
    list = list.filter((e) => {
      if (!e.date) return false;
      const d = typeof e.date === 'string' ? new Date(e.date) : e.date;
      if (Number.isNaN(d.getTime())) return false;
      return d.getFullYear() === yearFilter.value;
    });
  }

  list.sort((a, b) => {
    const da = a.date ? new Date(a.date) : null;
    const db = b.date ? new Date(b.date) : null;
    const ta = da && !Number.isNaN(da.getTime()) ? da.getTime() : 0;
    const tb = db && !Number.isNaN(db.getTime()) ? db.getTime() : 0;
    return sortOrder.value === 'desc' ? tb - ta : ta - tb;
  });

  return list;
});

const activeEntry = computed(() => filteredSortedEntries.value[activeIndex.value] || null);

const previewImageSrc = ref(null);
const previewImageAlt = ref('');

function getItemImageSrc(version, itemIndex, subIndex) {
  if (!version && version !== 0) return '';
  // Bilder liegen in public/changelog/<version>/<punkt>-<unterpunkt>.png
  // Beispiel: public/changelog/26.1.0-dev/4-1.png -> Unterpunkt 4.1
  const safeVersion = String(version).trim();
  if (!safeVersion) return '';
  return `/changelog/${safeVersion}/${itemIndex + 1}-${subIndex + 1}.png`;
}

function handleImageError(event) {
  const img = event?.target;
  if (img) {
    const figure = img.closest('.detail-item-figure');
    if (figure) {
      figure.style.display = 'none';
    } else {
      img.style.display = 'none';
    }
  }
}

function openImagePreview(src, alt) {
  previewImageSrc.value = src;
  previewImageAlt.value = alt || '';
}

function closeImagePreview() {
  previewImageSrc.value = null;
  previewImageAlt.value = '';
}

// Gibt den fortlaufenden (sections-übergreifenden) Item-Index zurück,
// damit Bildpfade wie 1-1.png, 2-1.png usw. konsistent bleiben.
function getSectionGlobalItemIndex(sections, sectionIndex, itemIndex) {
  let offset = 0;
  for (let s = 0; s < sectionIndex; s++) {
    offset += (sections[s]?.items?.length || 0);
  }
  return offset + itemIndex;
}

function selectEntry(index) {
  activeIndex.value = index;
  const entry = filteredSortedEntries.value[index];
  if (entry && unseenVersions.value.includes(entry.version)) {
    unseenVersions.value = unseenVersions.value.filter(v => v !== entry.version);
  }
}

function isLatest(entry) {
  return entry && entry.version === latestVersion.value;
}

function isBeta(entry) {
  return typeof entry?.version === 'string' && entry.version.toLowerCase().includes('beta');
}

function formatDateTime(value) {
  if (!value) return '';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

onMounted(() => {
  // Vor dem Markieren als gelesen die bisher ungesehenen Versionen merken (für Labels)
  unseenVersions.value = changelogStore.unseenVersions || [];
  // Beim Öffnen der Changelog-Seite alles als gelesen markieren
  changelogStore.markAllAsSeen();

  // Aktiv geöffnete Version sofort als „gesehen“ kennzeichnen (Label entfernen)
  const current = activeEntry.value;
  if (current && unseenVersions.value.includes(current.version)) {
    unseenVersions.value = unseenVersions.value.filter(v => v !== current.version);
  }
});
</script>

<template>
  <div class="reminder-wrapper singlepage-wrapper max-width">
    <main id="container-main">
      <preview-header
        title="Changelog"
        :to="{ name: 'Dashboard' }"
        button-label="Zurück zum Dashboard"
        arrow="left"
        dense
      >
        <template #summary-extra>
          <p class="subtitle">Alle Änderungen am SMA-Portal – kompakt und nach Version sortiert.</p>
        </template>
      </preview-header>

      <template v-if="!entries.length">
        <section class="empty-state">
          <p>Aktuell sind keine Changelog-Einträge vorhanden.</p>
        </section>
      </template>

      <template v-else>
        <section class="changelog-meta">
          <div class="changelog-meta-left">
            <span v-if="latestEntry" class="pill pill-version-mini">v{{ latestEntry.version }}</span>
            <span v-if="latestEntry?.date" class="pill pill-date pill-date-mini">{{ formatDateTime(latestEntry.date) }}</span>
            <span class="changelog-meta-label">Neueste Version</span>
          </div>
          <div class="changelog-meta-right">
            <span class="changelog-meta-count">{{ entries.length }} Versionen</span>
            <span v-if="unseenVersions.length" class="changelog-meta-unseen">{{ unseenVersions.length }} ungelesen</span>
          </div>
        </section>

        <section class="layout changelog-shell">
      <aside class="sidebar">
        <h2 class="sidebar-title">Versionen</h2>
        <div class="wave-wrapper">
          <div class="wave -one"></div>
          <div class="wave -two"></div>
          <div class="wave -three"></div>
        </div>
        <div class="sidebar-filters changelog-meta-controls">
          <select
            v-if="availableYears.length"
            v-model="yearFilter"
            class="meta-select"
          >
            <option value="all">Alle Jahre</option>
            <option
              v-for="year in availableYears"
              :key="year"
              :value="year"
            >
              {{ year }}
            </option>
          </select>
        </div>
        <ul class="sidebar-list">
          <li
            v-for="(entry, index) in filteredSortedEntries"
            :key="entry.version"
            class="sidebar-item-wrapper"
          >
            <button
              type="button"
              class="sidebar-item"
              :class="{ active: index === activeIndex }"
              @click="selectEntry(index)"
            >
              <div v-if="entry.date" class="sidebar-row-date">
                <span class="sidebar-entry-date pill pill-date pill-date-mini">{{ formatDateTime(entry.date) }}</span>
              </div>
              <div class="sidebar-row-main">
                <span class="pill pill-version-mini">v{{ entry.version }}</span>
                <span v-if="isLatest(entry)" class="badge badge-new">Neu</span>
                <span v-if="unseenVersions.includes(entry.version)" class="badge badge-unseen">Ungesehen</span>
              </div>
              <div class="sidebar-row-sub">
                <span class="sidebar-entry-title">{{ entry.title || 'Changelog' }}</span>
              </div>
            </button>
          </li>
        </ul>
      </aside>

      <article v-if="activeEntry" class="detail">
        <header class="detail-header">
          <div class="detail-pills">
            <span v-if="activeEntry.date" class="pill pill-date">{{ formatDateTime(activeEntry.date) }}</span>
            <span class="pill pill-version">v{{ activeEntry.version }}</span>
            <span v-if="isLatest(activeEntry)" class="pill pill-latest">Neueste Version</span>
            <span v-if="unseenVersions.includes(activeEntry.version)" class="pill pill-unseen">Ungesehen</span>
            <span v-if="isBeta(activeEntry)" class="pill pill-beta">Beta</span>
          </div>
          <h2 class="detail-title">{{ activeEntry.title || 'Changelog' }}</h2>
        </header>

        <template v-if="activeEntry.sections && activeEntry.sections.length">
          <section
            v-for="(section, si) in activeEntry.sections"
            :key="si"
            class="detail-section"
          >
            <h3 class="detail-section-title">{{ si + 1 }}. {{ section.title }}</h3>
            <ul class="detail-items-list">
              <li
                v-for="(item, itemIndex) in section.items"
                :key="item.heading"
                class="detail-item"
              >
                <h4>{{ item.heading }}</h4>
                <div class="detail-item-content">
                  <ul class="detail-subitems">
                    <li
                      v-for="(line, idx) in item.body"
                      :key="idx"
                      class="detail-subitem"
                    >
                      <div class="detail-subitem-text">{{ line }}</div>
                    </li>
                  </ul>
                  <figure class="detail-item-figure">
                    <img
                      class="detail-item-image"
                      :src="`/changelog/${activeEntry.version}/${si + 1}-${itemIndex + 1}.png`"
                      :alt="`Screenshot zu: ${item.heading}`"
                      loading="lazy"
                      @error="handleImageError"
                      @click="openImagePreview(`/changelog/${activeEntry.version}/${si + 1}-${itemIndex + 1}.png`, item.heading)"
                    >
                  </figure>
                </div>
              </li>
            </ul>
          </section>
        </template>
        <section v-else-if="activeEntry.items && activeEntry.items.length" class="detail-items">
          <ol>
            <li
              v-for="(item, itemIndex) in activeEntry.items"
              :key="item.heading"
              class="detail-item"
            >
              <h3>{{ item.heading }}</h3>
              <ul class="detail-subitems">
                <li
                  v-for="(line, idx) in item.body"
                  :key="idx"
                  class="detail-subitem"
                >
                  <div class="detail-subitem-text">
                    {{ line }}
                  </div>
                  <figure class="detail-item-figure">
                    <img
                      class="detail-item-image"
                      :src="getItemImageSrc(activeEntry.version, itemIndex, idx)"
                      :alt="`Beispiel oder Screenshot zu: ${line}`"
                      loading="lazy"
                      @error="handleImageError"
                      @click="openImagePreview(getItemImageSrc(activeEntry.version, itemIndex, idx), `Beispiel oder Screenshot zu: ${line}`)"
                    >
                  </figure>
                </li>
              </ul>
            </li>
          </ol>
        </section>
        <section v-else class="detail-empty">
          <p>Für diese Version sind noch keine Detail-Einträge hinterlegt.</p>
        </section>

        <div
          v-if="previewImageSrc"
          class="image-preview-backdrop"
          @click.self="closeImagePreview"
        >
          <div class="image-preview-dialog">
            <button
              type="button"
              class="image-preview-close"
              @click="closeImagePreview"
            >
              Schließen
            </button>
            <img
              :src="previewImageSrc"
              :alt="previewImageAlt || 'Changelog-Screenshot'"
              class="image-preview-image"
            >
            <p v-if="previewImageAlt" class="image-preview-caption">
              {{ previewImageAlt }}
            </p>
          </div>
        </div>
      </article>
    </section>
      </template>
    </main>
  </div>
</template>

<style scoped>
.singlepage-wrapper #container-main {
  flex-direction: column;
  gap:0;
}
.subtitle {
  margin: 0;
  opacity: 0.8;
  font-size: 0.9rem;
}

.empty-state {
  margin-top: 1.5rem;
  padding: 1.5rem;
  border-radius: 16px;
  border: 1px dashed rgba(148, 163, 184, 0.7);
  font-size: 0.9rem;
}

.changelog-meta {
  margin-top: 1.5rem;
  padding: 0.75rem 1rem;
  border-radius: 999px;
  border: 1px solid var(--sellwerk-color-main-neutral);
  background: var(--sellwerk-color-bg-container);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.changelog-meta-left {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
}

.changelog-meta-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  opacity: 0.8;
}

.changelog-meta-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.78rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.changelog-meta-count {
  opacity: 0.85;
}

.changelog-meta-unseen {
  font-weight: 600;
  color: var(--sellwerk-color-main-accent);
}

.changelog-meta-controls {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
  width: max-content;
  margin-bottom: .35rem;
}

.meta-toggle {
  border-radius: 999px;
  border: 1px solid var(--sellwerk-color-main-neutral);
  background: rgba(0, 0, 0, 0.16);
  color: #ffffff;
  font-size: 0.7rem;
  padding: 0.18rem 0.7rem;
  cursor: pointer;
}

.meta-toggle.active {
  border-color: var(--sellwerk-color-main-accent);
  background: #ffffff;
  color: var(--sellwerk-color-main-accent);
}

.meta-select {
  border-radius: 999px;
  border: 1px solid var(--sellwerk-color-main-neutral);
  background: var(--sellwerk-color-bg-container);
  font-size: 0.7rem;
  padding: 0.16rem 0.6rem;
  width: auto;
}

.layout {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  margin-top: 1.5rem;
}

.changelog-shell {
  margin-top: 1.75rem;
  width: 100%;
}

/* Auf dieser Seite soll der spezielle Main-Container kein Grid-Gap haben
   und die Inhalte (Header + Changelog-Layout) untereinander gestapelt werden */
#container-main.dashboard {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.sidebar {
  padding: 0.85rem 0.9rem 1rem;
  border-radius: 16px;
  border: 1px solid var(--sellwerk-color-main-neutral);
  background: var(--sellwerk-color-main-gradient);
  color: var(--sellwerk-color-font-main);
  /* flex: 0 0 500px; */
  max-width: 500px;
  width: 100%;
  height: auto;
  position: relative;
  overflow: hidden;
}

.sidebar li {
    max-width: 100%;
}

.theme--light .sidebar {
  background: var(--sellwerk-color-main-gradient);
  border-color: var(--sellwerk-color-main-neutral);
}

.sidebar-title {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  opacity: 0.8;
  margin: 0 0 0.75rem;
  color: #ffffff;
}

.sidebar-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.sidebar-item-wrapper {
  margin: 0;
}

.sidebar-item {
  width: 100%;
  border: 1px solid transparent;
  background: rgba(0, 0, 0, 0.15);
  border-radius: 12px;
  padding: 0.45rem 0.6rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
  color: inherit;
  text-align: left;
  transition: border-color 0.15s ease, background 0.15s ease, transform 0.08s ease;
}

.sidebar-item:hover {
  border-color: var(--sellwerk-color-secondary-accent);
  transform: translateY(-1px);
}

.sidebar-item.active {
  border-color: var(--sellwerk-color-main-accent);
  background: var(--sellwerk-color-main-gradient);
}

.theme--light .sidebar-item {
  background: rgba(255, 255, 255, 0.92);
}

.theme--light .sidebar-item.active {
  background: #ffffff;
  border-color: var(--sellwerk-color-main-accent);
}

.sidebar-row-main {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.sidebar-row-sub {
  display: flex;
  justify-content: flex-start;
  align-items: baseline;
  gap: 0.45rem;
  width: 100%;
}

.sidebar-row-date {
  margin-bottom: 0.25rem;
}

.sidebar-entry-title {
  font-size: 0.78rem;
  font-weight: 500;
}

.sidebar-entry-date {
  font-size: 0.7rem;
  opacity: 0.75;
}

.pill-date-mini {
  padding: 0.12rem 0.5rem;
  font-size: 0.68rem;
}

.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 0.12rem 0.45rem;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.badge-new {
  background: var(--color-sellwerk-grade-one);
  color: var(--sellwerk-color-light);
}

.badge-unseen {
  background: var(--sellwerk-color-main-accent);
  color: var(--sellwerk-color-light);
}

.detail {
  padding: 1.1rem 1.3rem 1.3rem;
  border-radius: 18px;
  border: 1px solid var(--sellwerk-color-main-neutral);
  background: var(--sellwerk-color-bg-container);
  flex: 1 1 auto;
}

.theme--light .detail {
  background: var(--sellwerk-color-bg-container);
  border-color: var(--sellwerk-color-main-neutral);
}

.detail-header {
  margin-bottom: 1rem;
}

.detail-section {
  margin-bottom: 1.5rem;
}

.detail-section-title {
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 0.75rem;
  padding-bottom: 0.35rem;
  border-bottom: 2px solid var(--sellwerk-color-main-neutral, #e5e7eb);
  color: var(--sellwerk-color-main, #111827);
}

.detail-items-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.detail-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.6rem;
}

.detail-title {
  margin: 0;
  font-size: 1.2rem;
}

.pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.16rem 0.6rem;
  font-size: 0.72rem;
  font-weight: 500;
  border: 1px solid transparent;
}

.pill-date {
  background: var(--sellwerk-color-main-neutral-second);
  border-color: var(--sellwerk-color-main-neutral);
}

.theme--light .pill-date {
  background: var(--sellwerk-color-main-neutral-second);
  border-color: var(--sellwerk-color-main-neutral);
}

.pill-version {
  background: var(--sellwerk-color-main);
  border-color: var(--sellwerk-color-tertiary);
  color: var(--sellwerk-color-light);
}

.pill-version-mini {
  background: var(--sellwerk-color-main);
  border-radius: 999px;
  padding: 0.08rem 0.45rem;
  font-size: 0.7rem;
}

.theme--light .pill-version,
.theme--light .pill-version-mini {
  background: var(--sellwerk-color-main);
  color: var(--sellwerk-color-light);
}

.pill-latest {
  background: var(--sellwerk-color-secondary-accent);
  color: var(--sellwerk-color-dark);
}

.pill-unseen {
  background: transparent;
  border-color: var(--sellwerk-color-main-accent);
  color: var(--sellwerk-color-main-accent);
}

.pill-beta {
  background: transparent;
  border-color: var(--sellwerk-color-main-accent);
  color: var(--sellwerk-color-main-accent);
}

.detail-items ol {
  margin: 0;
  padding-left: 1.15rem;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

/* In diesem View soll die globale 65ch-Begrenzung für li/p nicht greifen,
   damit Changelog-Punkte inkl. Screenshots etwas luftiger wirken. */
.detail-items,
.detail-items ol,
.detail-items li,
.detail-section,
.detail-items-list,
.detail-items-list li {
	max-width: none;
}

  .detail-item h3 {
  margin: 0 0 0.25rem;
  font-size: 0.98rem;
}

.detail-subitems {
  margin: 0.15rem 0 0;
  padding-left: 1.1rem;
  list-style-type: none;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.detail-subitem {
  position: relative;
  margin: 0;
  padding-left: 0.6rem;
  font-size: 0.86rem;
  line-height: 1.5;
  color: var(--sellwerk-color-font-main);
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.detail-subitem::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.55em;
  width: 0.3rem;
  height: 0.3rem;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.9);
}

.detail-subitem-text {
  margin: 0;
}

.detail-item-figure {
  margin: 0.75rem 0 0;
  max-width: 480px;
}

.detail-item-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.detail-item-image {
  display: block;
  width: 100%;
  height: auto;
  max-height: 360px;
  object-fit: contain;
  object-position: left top;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.6);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
  cursor: zoom-in;
  transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;
}

.detail-item-image:hover {
  transform: translateY(-2px) scale(1.01);
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.32);
  border-color: var(--sellwerk-color-main-accent);
}

.image-preview-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.image-preview-dialog {
  max-width: min(90vw, 1200px);
  max-height: 90vh;
  background: var(--sellwerk-color-bg-container);
  border-radius: 18px;
  padding: 0.75rem;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.45);
  border: 1px solid rgba(148, 163, 184, 0.8);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.image-preview-image {
  max-width: 100%;
  max-height: 70vh;
  border-radius: 14px;
  object-fit: contain;
}

.image-preview-caption {
  font-size: 0.8rem;
  opacity: 0.85;
}

.image-preview-close {
  align-self: flex-end;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.9);
  background: rgba(15, 23, 42, 0.85);
  color: #ffffff;
  font-size: 0.75rem;
  padding: 0.25rem 0.7rem;
  cursor: pointer;
}

@media (min-width: 900px) {
  .detail-subitem {
    flex-direction: row;
    align-items: flex-start;
  }

  .detail-subitem-text {
    flex: 1 1 auto;
    padding-right: 1.25rem;
  }

  .detail-item-figure {
    flex: 0 0 42%;
    max-width: none;
  }

  .detail-item-content {
    flex-direction: row;
    align-items: flex-start;
    gap: 0;
  }

  .detail-item-content .detail-subitems {
    flex: 1 1 auto;
    padding-right: 1.25rem;
  }

  .detail-item-content .detail-item-figure {
    flex: 0 0 42%;
    max-width: none;
    margin: 0;
  }
}

.detail-empty {
  padding: 1rem 0.25rem 0.25rem;
  font-size: 0.86rem;
  opacity: 0.8;
}

@media (max-width: 900px) {
  .layout {
    flex-direction: column;
  }

  .sidebar {
    order: 1;
  }

  .detail {
    order: 2;
  }
}

@media (max-width: 720px) {
  .changelog-shell {
    padding: 1.2rem 1rem 2.2rem;
  }
}
</style>
